import { useState, type DragEvent } from "react";
import type { DealListItemDto, DealPipelineStage, DealSubState } from "@/types/deals";
import { SUB_STATE_LABEL } from "@/types/deals";
import { cn } from "@/design-system/lib/utils";
import { DealCard, type DealCardAction } from "./DealCard";
import type { DroppedDealPayload, KanbanLookups } from "./KanbanColumn";

export interface SubStateSectionProps {
  stage: DealPipelineStage;
  /** When null, the section renders without a sub-state header — used for
   *  display-only stages (Underwriting / Closing / Closed) that have no
   *  sub-state breakdown. Drops are disabled in that case. */
  subState: DealSubState | null;
  items: DealListItemDto[];
  acceptsDrops: boolean;
  onDrop?: (payload: DroppedDealPayload) => void;
  cardActions?: DealCardAction[];
  lookups?: KanbanLookups;
}

export function SubStateSection({
  stage,
  subState,
  items,
  acceptsDrops,
  onDrop,
  cardActions,
  lookups,
}: SubStateSectionProps) {
  const [over, setOver] = useState(false);
  const droppable = acceptsDrops && subState !== null;
  const subStateLabel = subState ? SUB_STATE_LABEL[subState] : null;

  function readPayload(
    e: DragEvent<HTMLDivElement>,
  ): { id: string; stage: DealPipelineStage; subState: DealSubState } | null {
    const raw = e.dataTransfer.getData("application/x-phoenix-deal");
    if (!raw) return null;
    try {
      return JSON.parse(raw) as {
        id: string;
        stage: DealPipelineStage;
        subState: DealSubState;
      };
    } catch {
      return null;
    }
  }

  function handleDragOver(e: DragEvent<HTMLDivElement>) {
    if (!droppable) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (!over) setOver(true);
  }

  function handleDragLeave() {
    if (over) setOver(false);
  }

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    if (!droppable || !subState) return;
    e.preventDefault();
    setOver(false);
    const payload = readPayload(e);
    if (!payload || !onDrop) return;
    if (payload.stage === stage && payload.subState === subState) return;
    onDrop({
      id: payload.id,
      fromStage: payload.stage,
      fromSubState: payload.subState,
      toStage: stage,
      toSubState: subState,
    });
  }

  return (
    <section
      className="flex flex-col gap-space-2"
      data-stage={stage}
      data-substate={subState ?? "none"}
    >
      {subState && subStateLabel && (
        <header className="flex items-center justify-between px-space-1">
          <span className="font-display text-label font-semibold uppercase tracking-label text-onyx">
            {subStateLabel}
          </span>
          <span
            className="font-mono text-caption text-gray-mid tabular-nums"
            aria-label={`${items.length} deals`}
          >
            · {items.length}
          </span>
        </header>
      )}

      <div
        className={cn(
          "flex flex-col gap-space-2 rounded-md p-space-1",
          "transition-colors duration-fast ease-standard",
          droppable && "border border-dashed border-transparent",
          over && "border-gold bg-gold/5 ring-2 ring-gold/40",
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        role="list"
        aria-label={
          subStateLabel ? `${stage} ${subStateLabel} section` : `${stage} deals`
        }
      >
        {items.length === 0 ? (
          <div
            className="text-caption italic text-gray-mid px-space-2 py-space-2 text-center"
            aria-label={subStateLabel ? `No ${subStateLabel} deals` : "No deals"}
          >
            No deals at this sub-state
          </div>
        ) : (
          items.map((deal) => (
            <DealCard
              key={deal.id}
              deal={deal}
              draggable={acceptsDrops}
              actions={cardActions}
              brokerName={lookups?.brokerName?.(deal) ?? null}
              assigneeName={lookups?.assigneeName?.(deal) ?? null}
              hasOverdueDocs={lookups?.hasOverdueDocs?.(deal) ?? false}
              hasMissingRequiredFields={lookups?.hasMissingFields?.(deal) ?? false}
            />
          ))
        )}
      </div>
    </section>
  );
}
